'use client';

import { api, apiFetch } from '@/lib/utils/api';
import { Button, Row, Stack, Text } from '@slims/ui/atoms';
import {
	ConfirmationDialog,
	DetailField,
	DetailSection,
	DetailTextInput,
	InitialAvatar,
	InlineNotice,
	StatusBlock,
	SubPanel,
} from '@slims/ui/molecules';
import { PageContainer, PageHeader } from '@slims/ui/templates';
import { Save, Trash2, UserPlus, X } from 'lucide-react';
import { useParams, useRouter, useSearchParams } from '@/lib/next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AddCerUser } from '@/components/admin/add-cer-user';
import { type AdminPick, AdminUserPicker } from '@/components/admin/admin-user-picker';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { useAdminScopeStore } from '@/stores/admin-scope-store';
import { useAuthStore } from '@/stores/auth-store';

interface ConfigDetail {
	id: string;
	nome: string;
	parentId: string | null;
}

interface ConfigUser {
	id: string;
	email: string;
	fullName: string | null;
	role: string;
}

/**
 * Single configuration form for create (/configurations/new?cer=:id) and edit
 * (/configurations/:id). Kept intentionally light — only name + referent — to
 * mirror the CER form. The technical details (soggetto, CACER, impianto) are
 * managed separately from the list via a dedicated "Dettagli" action.
 */
export default function ConfigurationFormPage() {
	const router = useRouter();
	const params = useParams<{ id?: string }>();
	const searchParams = useSearchParams();
	const editId = params.id ?? null;
	const isEdit = !!editId;

	const { refetch: refetchScopeTree } = useScopeTree();
	const fetchAuth = useAuthStore((s) => s.fetchAuth);
	const setAdminScope = useAdminScopeStore((s) => s.setScope);

	const [cerId] = useState(searchParams.get('cer') ?? '');
	const [nome, setNome] = useState('');
	const [loading, setLoading] = useState(isEdit);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	// Edit-only state.
	const [referents, setReferents] = useState<ConfigUser[]>([]);
	const [pendingRemove, setPendingRemove] = useState<ConfigUser | null>(null);

	// Create-only optional initial referent (email-first; see AdminUserPicker).
	const [referentPick, setReferentPick] = useState<AdminPick>(emptyPick);

	const loadReferents = useCallback(async () => {
		if (!editId) return;
		const res = await apiFetch(`/api/admin/configs/${editId}/users`);
		if (!res.ok) return;
		const users: unknown = await res.json();
		if (Array.isArray(users))
			setReferents((users as ConfigUser[]).filter((u) => u.role === 'referent'));
	}, [editId]);

	useEffect(() => {
		if (!editId) return;
		Promise.all([api.get<ConfigDetail>(`/api/admin/configs/${editId}`), loadReferents()])
			.then(([data]) => {
				setNome(data.nome ?? '');
				setLoading(false);
			})
			.catch(() => setLoading(false));
	}, [editId, loadReferents]);

	async function handleCreate() {
		setError('');
		if (!cerId) {
			setError('Nessuna CER selezionata');
			return;
		}
		if (!nome.trim()) {
			setError('La denominazione è obbligatoria');
			return;
		}
		// The referent is optional: only validated/sent when a valid email was entered.
		if (referentPick.emailValid && !referentPick.existing && referentPick.password.length < 8) {
			setError('La password del referente deve avere almeno 8 caratteri');
			return;
		}
		const referentBody = !referentPick.emailValid
			? {}
			: referentPick.existing
				? { referentUserId: referentPick.existing.id }
				: {
						referentEmail: referentPick.email,
						referentPassword: referentPick.password,
						referentName: referentPick.fullName,
					};
		setSaving(true);
		const res = await apiFetch(`/api/admin/cers/${cerId}/configs`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ nome: nome.trim(), ...referentBody }),
		});
		if (!res.ok) {
			const body = (await res.json().catch(() => ({}))) as { error?: string };
			setError(body.error ?? 'Errore nella creazione');
			setSaving(false);
			return;
		}
		// La nuova configurazione (e la sua CER) devono comparire subito nei
		// selettori — Soci, Onboarding, header — senza aspettare il realtime o
		// un reload: `fetchAuth` porta la membership, `refetchScopeTree` l'albero.
		const created = (await res.json().catch(() => ({}))) as { id?: string };
		await Promise.all([fetchAuth(), refetchScopeTree()]);
		// Si torna sulla CER della configurazione appena creata, con quella
		// selezionata (INB-331): prima l'elenco riapriva la CER dello scope
		// precedente e la nuova configurazione non si vedeva.
		setAdminScope(cerId, created.id ?? null);
		router.push('/configurations');
	}

	async function handleSaveEdit() {
		setSaving(true);
		try {
			const res = await apiFetch(`/api/admin/configs/${editId}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ nome: nome.trim() }),
			});
			if (res.ok) {
				toast.success('Configurazione aggiornata');
				// Il nome cambia l'etichetta nei selettori: riallinea l'albero,
				// poi torna all'elenco.
				await refetchScopeTree();
				router.push('/configurations');
				return;
			}
			toast.error('Errore nel salvataggio');
		} catch {
			toast.error('Errore di rete');
		} finally {
			setSaving(false);
		}
	}

	async function confirmRemove() {
		if (!pendingRemove) return;
		const res = await apiFetch(`/api/admin/configs/${editId}/users/${pendingRemove.id}`, {
			method: 'DELETE',
		});
		if (res.ok) {
			setReferents((prev) => prev.filter((r) => r.id !== pendingRemove.id));
			toast.success('Referente rimosso');
		} else {
			toast.error('Errore nella rimozione');
		}
		setPendingRemove(null);
	}

	if (isEdit && loading) {
		return <StatusBlock kind="loading" size="lg" message="Caricamento..." />;
	}

	return (
		<PageContainer width="narrow">
			<Stack gap="xl">
				<PageHeader
					size="medium"
					title={isEdit ? nome || 'Configurazione' : 'Nuova Configurazione'}
					actions={
						<>
							<Button
								type="button"
								variant="outline"
								onClick={() => router.push('/configurations')}
								disabled={saving}
							>
								<X />
								Annulla
							</Button>
							<Button
								type="button"
								variant="accent"
								onClick={isEdit ? handleSaveEdit : handleCreate}
								disabled={saving || (!isEdit && referentPick.checking)}
							>
								<Save />
								{saving ? 'Salvataggio...' : isEdit ? 'Salva' : 'Crea Configurazione'}
							</Button>
						</>
					}
				/>

				{/* Dati configurazione */}
				<DetailSection title="Dati Configurazione">
					<DetailField label="Nome / Denominazione">
						<DetailTextInput value={nome} onChange={setNome} />
					</DetailField>
				</DetailSection>

				{/* Create: optional initial referent */}
				{!isEdit && (
					<DetailSection
						title="Referente (opzionale)"
						hint="Puoi assegnarlo ora oppure aggiungerlo in seguito dal dettaglio della configurazione."
					>
						<AdminUserPicker
							lookupUrl={`/api/admin/cers/${cerId}/user-lookup`}
							roleLabel="referente"
							onChange={setReferentPick}
							size="md"
						/>
					</DetailSection>
				)}

				{/* Edit: referents management */}
				{isEdit && (
					<ReferentsPanel
						configId={editId as string}
						referents={referents}
						onAdded={loadReferents}
						onRemove={setPendingRemove}
					/>
				)}

				{error && <InlineNotice tone="danger">{error}</InlineNotice>}

				<ConfirmationDialog
					open={!!pendingRemove}
					onOpenChange={(open) => {
						if (!open) setPendingRemove(null);
					}}
					title="Rimuovere il referente?"
					description={`${pendingRemove?.fullName || pendingRemove?.email} non sarà più referente di questa configurazione.`}
					confirmText="Sì, rimuovi"
					cancelText="Annulla"
					onConfirm={confirmRemove}
					variant="destructive"
				/>
			</Stack>
		</PageContainer>
	);
}

function ReferentsPanel({
	configId,
	referents,
	onAdded,
	onRemove,
}: {
	configId: string;
	referents: ConfigUser[];
	onAdded: () => void;
	onRemove: (u: ConfigUser) => void;
}) {
	const [open, setOpen] = useState(false);

	return (
		<DetailSection title="Referenti">
			{referents.length === 0 ? (
				<Text tone="muted">Nessun referente assegnato.</Text>
			) : (
				<Stack gap="sm">
					{referents.map((r) => (
						<Row key={r.id} gap="sm">
							<InitialAvatar tone="accent" initial={(r.fullName ?? r.email).charAt(0).toUpperCase()} />
							<Stack flex="one">
								<Text weight="semibold" truncate>
									{r.fullName ?? r.email}
								</Text>
								{r.fullName && (
									<Text variant="micro" tone="muted" truncate>
										{r.email}
									</Text>
								)}
							</Stack>
							<Button
								type="button"
								variant="ghostDanger"
								size="icon"
								title="Rimuovi"
								onClick={() => onRemove(r)}
							>
								<Trash2 />
							</Button>
						</Row>
					))}
				</Stack>
			)}

			{/* Sotto l'elenco, come «Aggiungi amministratore» nel dettaglio CER: da
			    solo in alto a destra lasciava una riga vuota sopra il testo (INB-374). */}
			<Row>
				<Button type="button" variant="accentSoft" size="sm" onClick={() => setOpen((v) => !v)}>
					<UserPlus /> Aggiungi referente
				</Button>
			</Row>

			{open && (
				<SubPanel title="Nuovo referente">
					<AddCerUser
						basePath={`/api/admin/configs/${configId}`}
						role="referent"
						roleLabel="referente"
						onAdded={() => {
							setOpen(false);
							onAdded();
						}}
					/>
				</SubPanel>
			)}
		</DetailSection>
	);
}

const emptyPick: AdminPick = {
	email: '',
	fullName: '',
	password: '',
	existing: null,
	emailValid: false,
	checking: false,
};
